import { useEffect, useState, useMemo, type FormEvent } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  FormField,
  Input,
  LoadingState,
  Select,
} from '@sakhtar/design-system/react';
import {
  createOperatorPlan,
  listOperatorPlans,
  setOperatorPlanActive,
  type OperatorPlan,
} from '../../lib/operatorApi.js';
import { OperatorPage } from './OperatorShell.js';

export const ALL_ERP_MODULES = [
  { key: 'crm-sales', label: 'فروش و CRM' },
  { key: 'procurement-inventory', label: 'انبار و موجودی' },
  { key: 'procurement', label: 'خرید و تأمین' },
  { key: 'accounting', label: 'مالی و حسابداری' },
  { key: 'projects', label: 'پروژه‌ها و کارها' },
  { key: 'master-data', label: 'اطلاعات پایه' },
  { key: 'foundation', label: 'زیرساخت پایه' },
  { key: 'app:salon-showcase', label: 'اپلیکیشن سالن زیبایی (Showcase)' },
] as const;

export interface PlanTemplate {
  readonly id: string;
  readonly label: string;
  readonly icon: string;
  readonly displayName: string;
  readonly billingInterval: 'monthly' | 'annual' | 'custom';
  readonly basePriceRial: string;
  readonly extraSeatPriceRial: string;
  readonly includedSeats: number;
  readonly trialEnabled: boolean;
  readonly trialDurationDays: number | null;
  readonly trialSeatLimit: number | null;
  readonly moduleAllowlist: string[];
  readonly postTrialBehavior: 'readonly' | 'suspended';
  readonly summary: string;
}

export const PLAN_PRESETS: readonly PlanTemplate[] = [
  {
    id: 'salon_showcase',
    label: 'طرح سالن زیبایی (Showcase)',
    icon: '💇‍♀️',
    displayName: 'طرح سالن زیبایی (Salon Showcase)',
    billingInterval: 'monthly',
    basePriceRial: '0',
    extraSeatPriceRial: '0',
    includedSeats: 5,
    trialEnabled: true,
    trialDurationDays: 30,
    trialSeatLimit: 5,
    moduleAllowlist: ['app:salon-showcase'],
    postTrialBehavior: 'readonly',
    summary: '۳۰ روزه رایگان، ۵ کاربر، اختصاصی سالن زیبایی',
  },
  {
    id: 'trial_free',
    label: 'طرح آزمایشی رایگان',
    icon: '🌟',
    displayName: 'ساختار کلود — آزمایشی ۱۵ روزه',
    billingInterval: 'monthly',
    basePriceRial: '0',
    extraSeatPriceRial: '0',
    includedSeats: 3,
    trialEnabled: true,
    trialDurationDays: 15,
    trialSeatLimit: 5,
    moduleAllowlist: ALL_ERP_MODULES.map((m) => m.key),
    postTrialBehavior: 'readonly',
    summary: '۱۵ روزه رایگان، سقف ۵ کاربر، تمام ماژول‌ها',
  },
  {
    id: 'standard_monthly',
    label: 'طرح استاندارد ماهانه',
    icon: '💼',
    displayName: 'طرح استاندارد ماهانه',
    billingInterval: 'monthly',
    basePriceRial: '120000000', // 12M Tomans
    extraSeatPriceRial: '5000000', // 500k Tomans
    includedSeats: 10,
    trialEnabled: true,
    trialDurationDays: 15,
    trialSeatLimit: 10,
    moduleAllowlist: ALL_ERP_MODULES.map((m) => m.key),
    postTrialBehavior: 'readonly',
    summary: '۱۲ میلیون تومان/ماه، ۱۰ کاربر، تمام ماژول‌ها',
  },
  {
    id: 'standard_annual',
    label: 'طرح استاندارد سالانه (تخفیف ویژه)',
    icon: '🚀',
    displayName: 'طرح استاندارد سالانه',
    billingInterval: 'annual',
    basePriceRial: '1200000000', // 120M Tomans (2 months free)
    extraSeatPriceRial: '50000000', // 5M Tomans
    includedSeats: 10,
    trialEnabled: true,
    trialDurationDays: 15,
    trialSeatLimit: 10,
    moduleAllowlist: ALL_ERP_MODULES.map((m) => m.key),
    postTrialBehavior: 'readonly',
    summary: '۱۲۰ میلیون تومان/سال (۲ ماه رایگان)، ۱۰ کاربر',
  },
  {
    id: 'enterprise_annual',
    label: 'طرح سازمانی (Enterprise)',
    icon: '🏢',
    displayName: 'طرح سازمانی (Enterprise)',
    billingInterval: 'annual',
    basePriceRial: '2500000000', // 250M Tomans
    extraSeatPriceRial: '40000000', // 4M Tomans
    includedSeats: 30,
    trialEnabled: true,
    trialDurationDays: 15,
    trialSeatLimit: 30,
    moduleAllowlist: ALL_ERP_MODULES.map((m) => m.key),
    postTrialBehavior: 'readonly',
    summary: '۲۵۰ میلیون تومان/سال، ۳۰ کاربر مشمول، پشتیبانی اختصاصی',
  },
];

function formatRialAndToman(rialStr: string): string {
  try {
    const rial = BigInt(rialStr || '0');
    if (rial === 0n) return 'رایگان (۰ ریال)';
    const toman = rial / 10n;
    return `${toman.toLocaleString('fa-IR')} تومان (${rial.toLocaleString('fa-IR')} ریال)`;
  } catch {
    return rialStr;
  }
}

function toTomanPreview(rialStr: string): string {
  try {
    const rial = BigInt(rialStr || '0');
    if (rial === 0n) return 'رایگان';
    const toman = rial / 10n;
    return `معادل ${toman.toLocaleString('fa-IR')} تومان`;
  } catch {
    return '';
  }
}

function intervalBadge(interval: string) {
  switch (interval) {
    case 'monthly':
      return <Badge tone="info">ماهانه</Badge>;
    case 'annual':
      return <Badge tone="success">سالانه</Badge>;
    case 'custom':
      return <Badge tone="default">سفارشی</Badge>;
    default:
      return <Badge tone="default">{interval}</Badge>;
  }
}

/**
 * UI_REFERENCE_SOURCE:
 * section: (no dedicated board — see PRODUCTION_READINESS_AUDIT.md's
 *   "Pilot Admin Console" section: the 7 `internal-admin/*` operator-console
 *   files, including this one, are Cloud ERP's own cross-tenant Super Admin
 *   console with no frame anywhere in the design bundle; `Sakhtar Pilot
 *   P0.html`'s admin console is the different, already-cited tenant-scoped
 *   `/t/:slug/admin/*` surface.)
 * canonical pattern: v1 `Card`/`FormField`/`Input`/`Select`/`DataTable`,
 *   matching every other `internal-admin/*` page — not mixed with v2.
 * state reference: loading (`rows === null`) · error · success · ready
 *
 * Required-field marking checked against
 * `assertCreatePlanInput`/`assertTrialOverrideInput`
 * (`apps/api/src/operator/runtime.ts`): every field here that the backend
 * requires a valid, always-present value for (including
 * `postTrialBehavior`, which has no legal "unset" state) is now marked
 * `required`. Money fields (`basePriceRial`/`extraSeatPriceRial`) are
 * deliberately plain `dir="ltr"` text inputs, not `type="number"` — avoids
 * the float-precision risk of a native number input on large Rial digit
 * strings (AGENTS.md §8.3); previews use `BigInt`, never `Number()`.
 */
export function PlansPage() {
  const [rows, setRows] = useState<readonly OperatorPlan[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form states
  const [displayName, setDisplayName] = useState('');
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annual' | 'custom'>(
    'monthly',
  );
  const [basePriceRial, setBasePriceRial] = useState('120000000');
  const [extraSeatPriceRial, setExtraSeatPriceRial] = useState('5000000');
  const [includedSeats, setIncludedSeats] = useState('10');
  const [trialEnabled, setTrialEnabled] = useState(true);
  const [trialDurationDays, setTrialDurationDays] = useState('15');
  const [trialSeatLimit, setTrialSeatLimit] = useState('10');
  const [selectedModules, setSelectedModules] = useState<string[]>(
    ALL_ERP_MODULES.map((m) => m.key),
  );
  const [postTrialBehavior, setPostTrialBehavior] = useState<'readonly' | 'suspended'>('readonly');

  const [pending, setPending] = useState<{ planRef: string; active: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  async function reload() {
    const plans = await listOperatorPlans(true);
    setRows(plans);
  }

  useEffect(() => {
    void reload().catch(() => setError('بارگذاری طرح‌ها ناموفق بود.'));
  }, []);

  function applyPreset(preset: PlanTemplate) {
    setDisplayName(preset.displayName);
    setBillingInterval(preset.billingInterval);
    setBasePriceRial(preset.basePriceRial);
    setExtraSeatPriceRial(preset.extraSeatPriceRial);
    setIncludedSeats(String(preset.includedSeats));
    setTrialEnabled(preset.trialEnabled);
    setTrialDurationDays(String(preset.trialDurationDays ?? 15));
    setTrialSeatLimit(String(preset.trialSeatLimit ?? 10));
    setSelectedModules([...preset.moduleAllowlist]);
    setPostTrialBehavior(preset.postTrialBehavior);
  }

  function toggleModule(key: string) {
    setSelectedModules((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }

  function selectAllModules() {
    setSelectedModules(ALL_ERP_MODULES.map((m) => m.key));
  }

  function clearAllModules() {
    setSelectedModules([]);
  }

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await createOperatorPlan({
        displayName: displayName.trim(),
        billingInterval,
        basePriceRial: basePriceRial.trim() || '0',
        extraSeatPriceRial: extraSeatPriceRial.trim() || '0',
        includedSeats: Number(includedSeats) || 0,
        trialEnabled,
        trialDurationDays: trialEnabled ? Number(trialDurationDays) || 15 : null,
        trialSeatLimit: trialEnabled ? Number(trialSeatLimit) || 10 : null,
        moduleAllowlist: selectedModules.length > 0 ? selectedModules : null,
        postTrialBehavior,
      });
      setSuccess(`طرح «${displayName.trim()}» با موفقیت ایجاد شد.`);
      setDisplayName('');
      await reload();
    } catch {
      setError('ایجاد طرح ناموفق بود. لطفاً از یکتا بودن نام و صحت مقادیر اطمینان حاصل کنید.');
    } finally {
      setBusy(false);
    }
  }

  async function applyActive() {
    if (!pending) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await setOperatorPlanActive(pending.planRef, pending.active);
      setSuccess(pending.active ? 'طرح با موفقیت فعال شد.' : 'طرح با موفقیت غیرفعال شد.');
      await reload();
    } catch {
      setError('تغییر وضعیت طرح ناموفق بود.');
    } finally {
      setBusy(false);
      setPending(null);
    }
  }

  const stats = useMemo(() => {
    if (!rows) return null;
    const total = rows.length;
    const active = rows.filter((r) => r.active).length;
    const trial = rows.filter((r) => r.trialEnabled).length;
    return { total, active, trial };
  }, [rows]);

  return (
    <OperatorPage title="مدیریت طرح‌های اشتراک (Plans)">
      {error ? (
        <Alert tone="danger" title="خطا">
          {error}
        </Alert>
      ) : null}

      {success ? (
        <Alert tone="success" title="موفق">
          {success}
        </Alert>
      ) : null}

      {/* خلاصه وضعیت آماری */}
      {stats ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 16,
            marginBottom: 20,
          }}
        >
          <Card title="کل طرح‌ها">
            <span style={{ fontSize: 24, fontWeight: 'bold' }}>{stats.total}</span>
          </Card>
          <Card title="طرح‌های فعال">
            <span style={{ fontSize: 24, fontWeight: 'bold', color: '#10b981' }}>
              {stats.active}
            </span>
          </Card>
          <Card title="طرح‌های دارای آزمایشی">
            <span style={{ fontSize: 24, fontWeight: 'bold', color: '#3b82f6' }}>
              {stats.trial}
            </span>
          </Card>
        </div>
      ) : null}

      {/* قالب‌های آماده طرح‌های سابسکریپشن */}
      <Card title="قالب‌های آماده طرح‌های سابسکریپشن (درج سریع)">
        <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--sk-text-secondary, #6b7280)' }}>
          برای تکمیل خودکار فرم ایجاد طرح با مقادیر استاندارد سیستم، یکی از طرح‌های زیر را انتخاب
          کنید:
        </p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 12,
          }}
        >
          {PLAN_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              onClick={() => applyPreset(preset)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 6,
                padding: '12px 14px',
                borderRadius: 8,
                border: '1px solid var(--sk-border, #e5e7eb)',
                background: 'var(--sk-surface-card, #ffffff)',
                cursor: 'pointer',
                textAlign: 'right',
                transition: 'all 0.15s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                <span style={{ fontSize: 20 }}>{preset.icon}</span>
                <strong style={{ fontSize: 14 }}>{preset.label}</strong>
              </div>
              <span style={{ fontSize: 12, color: 'var(--sk-text-secondary, #6b7280)' }}>
                {preset.summary}
              </span>
            </button>
          ))}
        </div>
      </Card>

      {/* فرم ایجاد / تعریف طرح جدید */}
      <Card title="تعریف و افزودن طرح اشتراک جدید">
        <form onSubmit={onCreate} style={{ display: 'grid', gap: 16, maxWidth: 640 }}>
          <FormField label="نام نمایشی طرح" required>
            <Input
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="مثلاً: طرح استاندارد ماهانه"
              required
            />
          </FormField>

          <FormField label="دوره صورت‌حساب" required>
            <Select
              value={billingInterval}
              onChange={(event) =>
                setBillingInterval(event.target.value as 'monthly' | 'annual' | 'custom')
              }
              required
            >
              <option value="monthly">ماهانه (Monthly)</option>
              <option value="annual">سالانه (Annual)</option>
              <option value="custom">سفارشی / دوره‌ای (Custom)</option>
            </Select>
          </FormField>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label="قیمت پایه دوره (ریال)" hint={toTomanPreview(basePriceRial)} required>
              <Input
                dir="ltr"
                value={basePriceRial}
                onChange={(event) => setBasePriceRial(event.target.value)}
                placeholder="مثلاً: 120000000"
                required
              />
            </FormField>

            <FormField
              label="قیمت هر صندلی اضافه (ریال)"
              hint={toTomanPreview(extraSeatPriceRial)}
              required
            >
              <Input
                dir="ltr"
                value={extraSeatPriceRial}
                onChange={(event) => setExtraSeatPriceRial(event.target.value)}
                placeholder="مثلاً: 5000000"
                required
              />
            </FormField>
          </div>

          <FormField label="تعداد کاربران مشمول در قیمت پایه" required>
            <Input
              type="number"
              min={0}
              value={includedSeats}
              onChange={(event) => setIncludedSeats(event.target.value)}
              required
            />
          </FormField>

          {/* دوره آزمایشی */}
          <div
            style={{
              padding: 12,
              borderRadius: 8,
              border: '1px solid var(--sk-border, #e5e7eb)',
              background: 'var(--sk-surface-subtle, #f9fafb)',
              display: 'grid',
              gap: 12,
            }}
          >
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              <input
                type="checkbox"
                checked={trialEnabled}
                onChange={(event) => setTrialEnabled(event.target.checked)}
                style={{ width: 18, height: 18, accentColor: '#2563eb' }}
              />
              فعال‌سازی دوره آزمایشی رایگان برای این طرح
            </label>

            {trialEnabled ? (
              <div
                style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 4 }}
              >
                <FormField label="مدت زمان دوره آزمایشی (روز)" required>
                  <Input
                    type="number"
                    min={1}
                    value={trialDurationDays}
                    onChange={(event) => setTrialDurationDays(event.target.value)}
                    required
                  />
                </FormField>

                <FormField label="سقف مجاز کاربران آزمایشی" required>
                  <Input
                    type="number"
                    min={1}
                    value={trialSeatLimit}
                    onChange={(event) => setTrialSeatLimit(event.target.value)}
                    required
                  />
                </FormField>
              </div>
            ) : null}

            {/* `postTrialBehavior` must always be exactly 'readonly' | 'suspended'
                server-side (assertCreatePlanInput, apps/api/src/operator/runtime.ts) —
                there is no valid "unset" state, matching the sibling
                "دوره صورت‌حساب" field's `required` marking above. */}
            <FormField label="رفتار پس از اتمام دوره آزمایشی" required>
              <Select
                value={postTrialBehavior}
                onChange={(event) =>
                  setPostTrialBehavior(event.target.value as 'readonly' | 'suspended')
                }
                required
              >
                <option value="readonly">فقط خواندنی (مشاهده اطلاعات بدون دسترسی ثبت جدید)</option>
                <option value="suspended">مسدودسازی و تعلیق ورود تا پرداخت</option>
              </Select>
            </FormField>
          </div>

          {/* ماژول‌های مجاز طرح */}
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={{ fontSize: 14, fontWeight: 600 }}>
                ماژول‌های مجاز و در دسترس این طرح:
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button size="sm" variant="ghost" type="button" onClick={selectAllModules}>
                  انتخاب همه
                </Button>
                <Button size="sm" variant="ghost" type="button" onClick={clearAllModules}>
                  حذف همه
                </Button>
              </div>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 8,
                padding: 12,
                borderRadius: 8,
                border: '1px solid var(--sk-border, #e5e7eb)',
              }}
            >
              {ALL_ERP_MODULES.map((mod) => (
                <label
                  key={mod.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    fontSize: 13,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedModules.includes(mod.key)}
                    onChange={() => toggleModule(mod.key)}
                    style={{ width: 16, height: 16, accentColor: '#2563eb' }}
                  />
                  {mod.label}
                </label>
              ))}
            </div>
          </div>

          <Button type="submit" loading={busy}>
            افزودن و اتصال طرح جدید
          </Button>
        </form>
      </Card>

      {/* جدول طرح‌های موجود در کاتالوگ پلتفرم */}
      <Card title="فهرست طرح‌های کاتالوگ سیستم">
        {rows === null ? (
          <LoadingState />
        ) : (
          <DataTable
            columns={[
              {
                key: 'name',
                header: 'نام طرح',
                render: (row) => (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <strong>{row.displayName}</strong>
                    <span style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>
                      {row.planRef.slice(0, 8)}
                    </span>
                  </div>
                ),
              },
              {
                key: 'interval',
                header: 'دوره',
                render: (row) => intervalBadge(row.billingInterval),
              },
              {
                key: 'price',
                header: 'قیمت پایه',
                render: (row) => formatRialAndToman(row.basePriceRial),
              },
              {
                key: 'seats',
                header: 'صندلی‌ها',
                render: (row) => (
                  <div style={{ fontSize: 13 }}>
                    <div>{row.includedSeats} کاربر مشمول</div>
                    {row.extraSeatPriceRial !== '0' ? (
                      <div style={{ fontSize: 11, color: '#6b7280' }}>
                        + {toTomanPreview(row.extraSeatPriceRial)} صندلی اضافه
                      </div>
                    ) : null}
                  </div>
                ),
              },
              {
                key: 'trial',
                header: 'آزمایشی',
                render: (row) =>
                  row.trialEnabled ? (
                    <Badge tone="info">
                      {row.trialDurationDays} روز (سقف {row.trialSeatLimit} کاربر)
                    </Badge>
                  ) : (
                    <span style={{ color: '#9ca3af' }}>ندارد</span>
                  ),
              },
              {
                key: 'active',
                header: 'وضعیت',
                render: (row) => (
                  <Badge tone={row.active ? 'success' : 'default'}>
                    {row.active ? 'فعال' : 'غیرفعال'}
                  </Badge>
                ),
              },
              {
                key: 'toggle',
                header: 'عملیات',
                render: (row) => (
                  <Button
                    type="button"
                    size="sm"
                    variant={row.active ? 'danger' : 'secondary'}
                    onClick={() => setPending({ planRef: row.planRef, active: !row.active })}
                  >
                    {row.active ? 'غیرفعال‌سازی' : 'فعال‌سازی'}
                  </Button>
                ),
              },
            ]}
            rows={[...rows]}
            rowKey={(row) => row.planRef}
          />
        )}
      </Card>

      <ConfirmDialog
        open={pending !== null}
        title="تغییر وضعیت طرح"
        message="تغییر وضعیت این طرح بر روی ایجاد سازمان‌های جدید و ثبت اشتراک اثرگذار خواهد بود."
        danger={pending?.active === false}
        loading={busy}
        onCancel={() => setPending(null)}
        onConfirm={() => void applyActive()}
      />
    </OperatorPage>
  );
}
